import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ProNavigation } from "@/components/ProNavigation";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { format } from "date-fns";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Eye,
  ArrowLeft,
  Save,
  Camera,
  FileText,
  Download,
  Trash2,
  Sparkles,
  User,
  Calendar,
} from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { IrisCameraCapture } from "@/components/iridology/IrisCameraCapture";
import { IrisPhotoGallery } from "@/components/iridology/IrisPhotoGallery";
import { IridologyAnalysisForm } from "@/components/iridology/IridologyAnalysisForm";
import { IridologyFormData, defaultIridologyFormData, IrisPhoto, IridologyRecommendation } from "@/types/iridology";
import { IridologyRecommendations } from "@/components/iridology/IridologyRecommendations";
import { IrisInteractiveMap } from "@/components/iridology/IrisInteractiveMap";
import { IrisComparisonView } from "@/components/iridology/IrisComparisonView";
import { generateFullSessionPdf } from "@/utils/fullSessionPdfExport";
import { SelectedZoneEntry } from "@/components/iridology/SelectedZonesPanel";
import { IridologyObservationsForm, IridologyObservationsData, defaultObservationsData } from "@/components/iridology/IridologyObservationsForm";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface Patient {
  id: string;
  name: string;
}

const IridologySession = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === "new";
  
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [formData, setFormData] = useState<IridologyFormData>(defaultIridologyFormData);
  const [photos, setPhotos] = useState<IrisPhoto[]>([]);
  const [recommendations, setRecommendations] = useState<IridologyRecommendation[]>([]);
  const [patientName, setPatientName] = useState<string>("");
  const [activeTab, setActiveTab] = useState("info");
  const [sessionIdState, setSessionIdState] = useState<string | null>(id === "new" ? null : id || null);
  const [selectedZonesForExport, setSelectedZonesForExport] = useState<SelectedZoneEntry[]>([]);
  const [zoneObservations, setZoneObservations] = useState<any[]>([]);
  const [rightEyeData, setRightEyeData] = useState<{ imageData: string | null; calibration: any } | null>(null);
  const [leftEyeData, setLeftEyeData] = useState<{ imageData: string | null; calibration: any } | null>(null);
  const [observationsData, setObservationsData] = useState<IridologyObservationsData>(defaultObservationsData);
  const [observationsId, setObservationsId] = useState<string | null>(null);

  const handleEyeDataChange = useCallback((right: any, left: any) => {
    setRightEyeData({ imageData: right.imageData, calibration: right.calibration });
    setLeftEyeData({ imageData: left.imageData, calibration: left.calibration });
  }, []);

  useEffect(() => {
    loadPatients();
    if (!isNew && id) {
      loadSession(id);
    }
  }, [id, isNew]);

  const loadPatients = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from("patients")
        .select("id, name")
        .eq("user_id", user.id)
        .order("name");

      if (error) throw error;
      setPatients(data || []);
    } catch (error: any) {
      console.error("Erreur chargement patients:", error);
    }
  };

  const loadSession = async (sessionId: string) => {
    try {
      const { data: session, error } = await supabase
        .from("iridology_sessions")
        .select(`
          *,
          patient:patients(name),
          iris_photos(*)
        `)
        .eq("id", sessionId)
        .single();

      if (error) throw error;

      if (session) {
        setFormData({
          patient_id: session.patient_id,
          session_date: session.session_date,
          consultation_type: session.consultation_type,
          eye_observed: session.eye_observed,
          patient_gender: (session as any).patient_gender || null,
          is_pregnant: (session as any).is_pregnant || false,
          is_breastfeeding: (session as any).is_breastfeeding || false,
          terrain_nervous: session.terrain_nervous || false,
          terrain_digestive: session.terrain_digestive || false,
          terrain_circulatory: session.terrain_circulatory || false,
          terrain_eliminatory: session.terrain_eliminatory || false,
          terrain_mixed: session.terrain_mixed || false,
          vitality_level: session.vitality_level,
          vitality_comments: session.vitality_comments || '',
          adaptation_good: session.adaptation_good || false,
          adaptation_stress_sensitivity: session.adaptation_stress_sensitivity || false,
          adaptation_fatigue: session.adaptation_fatigue || false,
          sensitivity_zones: session.sensitivity_zones || '',
          support_zones: session.support_zones || '',
          overload_digestive: session.overload_digestive || false,
          overload_nervous: session.overload_nervous || false,
          overload_emotional: session.overload_emotional || false,
          overload_eliminatory: session.overload_eliminatory || false,
          overload_circulatory: session.overload_circulatory || false,
          axis_hygiene: session.axis_hygiene || false,
          axis_nutrition: session.axis_nutrition || false,
          axis_plants: session.axis_plants || false,
          axis_stress: session.axis_stress || false,
          axis_seasonal: session.axis_seasonal || false,
          additional_observations: session.additional_observations || '',
        });
        setPatientName(session.patient?.name || "");
        const loadedPhotos: IrisPhoto[] = session.iris_photos || [];
        setPhotos(loadedPhotos);
        
        // Populate eye data from saved photos for PDF export
        const rightPhoto = loadedPhotos.find((p: IrisPhoto) => p.eye_side === "D" || p.eye_side === "right");
        const leftPhoto = loadedPhotos.find((p: IrisPhoto) => p.eye_side === "G" || p.eye_side === "left");
        if (rightPhoto) {
          setRightEyeData({ imageData: rightPhoto.photo_url, calibration: null });
        }
        if (leftPhoto) {
          setLeftEyeData({ imageData: leftPhoto.photo_url, calibration: null });
        }
        
        // Parse recommendations from JSON
        const recs = session.recommendations as any[];
        if (recs && Array.isArray(recs)) {
          setRecommendations(recs.map((r: any) => ({
            id: r.id || `rec_${Date.now()}`,
            category: r.category || '',
            title: r.title || '',
            description: r.description || '',
            priority: r.priority || 0,
          })));
        }

        // Load observations data
        await loadObservations(sessionId);
      }
    } catch (error: any) {
      console.error("Erreur chargement séance:", error);
      toast.error("Erreur lors du chargement de la séance");
    } finally {
      setLoading(false);
    }
  };

  const loadObservations = async (sessionId: string) => {
    try {
      const { data, error } = await supabase
        .from("iridology_observations")
        .select("*")
        .eq("session_id", sessionId)
        .single();

      if (error && error.code !== "PGRST116") throw error;

      if (data) {
        setObservationsId(data.id);
        setObservationsData({
          elements_remarquables: data.elements_remarquables || '',
          aspect_general: (data.aspect_general as IridologyObservationsData['aspect_general']) || null,
          couleur: (data.couleur as IridologyObservationsData['couleur']) || null,
          trame: (data.trame as IridologyObservationsData['trame']) || null,
          pupilles: (data.pupilles as IridologyObservationsData['pupilles']) || null,
          anisocorie: data.anisocorie || false,
          heterochromie: data.heterochromie || false,
          strabisme: data.strabisme || false,
          debut_cataracte: data.debut_cataracte || false,
          autre_observation: data.autre_observation || '',
          myopie: data.myopie || false,
          hypermetropie: data.hypermetropie || false,
          astigmatisme: data.astigmatisme || false,
          presbytie: data.presbytie || false,
          autre_defaut_vision: data.autre_defaut_vision || '',
          lunettes_vue: data.lunettes_vue || false,
          lunettes_vue_quand: data.lunettes_vue_quand || '',
          lentilles: data.lentilles || false,
          lunettes_repos: data.lunettes_repos || false,
          constitution: (data.constitution as IridologyObservationsData['constitution']) || null,
          sous_constitution: (data.sous_constitution as IridologyObservationsData['sous_constitution']) || null,
          trame_od: data.trame_od || null,
          trame_og: data.trame_og || null,
          relief_od: (data.relief_od as IridologyObservationsData['relief_od']) || null,
          relief_og: (data.relief_og as IridologyObservationsData['relief_og']) || null,
          cercle_pupillaire_visible: data.cercle_pupillaire_visible || '',
          parasympathique: (data.parasympathique as IridologyObservationsData['parasympathique']) || null,
          orthosympathique: (data.orthosympathique as IridologyObservationsData['orthosympathique']) || null,
          pupille_d_anisocorie: data.pupille_d_anisocorie || false,
          pupille_d_diametre: (data.pupille_d_diametre as IridologyObservationsData['pupille_d_diametre']) || null,
          pupille_d_bords_grignotes: data.pupille_d_bords_grignotes || false,
          pupille_d_bords_cabosses: data.pupille_d_bords_cabosses || false,
          pupille_d_meplats: data.pupille_d_meplats || '',
          pupille_d_ovalisation: (data.pupille_d_ovalisation as IridologyObservationsData['pupille_d_ovalisation']) || null,
          pupille_d_ovalisation_secteurs: data.pupille_d_ovalisation_secteurs || '',
          pupille_d_decentrement: (data.pupille_d_decentrement as IridologyObservationsData['pupille_d_decentrement']) || null,
          pupille_d_decentrement_secteurs: data.pupille_d_decentrement_secteurs || '',
          pupille_g_diametre: (data.pupille_g_diametre as IridologyObservationsData['pupille_g_diametre']) || null,
          pupille_g_bords_grignotes: data.pupille_g_bords_grignotes || false,
          pupille_g_bords_cabosses: data.pupille_g_bords_cabosses || false,
          pupille_g_meplats: data.pupille_g_meplats || '',
          pupille_g_ovalisation: (data.pupille_g_ovalisation as IridologyObservationsData['pupille_g_ovalisation']) || null,
          pupille_g_ovalisation_secteurs: data.pupille_g_ovalisation_secteurs || '',
          pupille_g_decentrement: (data.pupille_g_decentrement as IridologyObservationsData['pupille_g_decentrement']) || null,
          pupille_g_decentrement_secteurs: data.pupille_g_decentrement_secteurs || '',
          acide_oxalique: data.acide_oxalique || false,
          acide_pyruvique: data.acide_pyruvique || false,
          acide_urique: data.acide_urique || false,
          anneau_electrolytique: data.anneau_electrolytique || false,
          anneau_lipidique: data.anneau_lipidique || false,
          anneau_gerontoxon: data.anneau_gerontoxon || false,
          taches_psores: data.taches_psores || false,
          taches_cholesterol: data.taches_cholesterol || false,
          taches_cicatrisation: data.taches_cicatrisation || false,
          taches_stress: data.taches_stress || false,
          taches_hemorragique: data.taches_hemorragique || false,
          radii_solari: data.radii_solari || false,
          arcs_cercle: data.arcs_cercle || false,
          vacuoles: data.vacuoles || false,
          lacunes: data.lacunes || false,
          cryptes: data.cryptes || false,
          conclusions_psy: data.conclusions_psy || '',
        });
      }
    } catch (error: any) {
      console.error("Erreur chargement observations:", error);
    }
  };

  const handleFormChange = (changes: Partial<IridologyFormData>) => {
    setFormData(prev => ({ ...prev, ...changes }));
  };

  const handleSave = async () => {
    if (!formData.patient_id) {
      toast.error("Veuillez sélectionner un consultant");
      return;
    }

    setSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Non authentifié");

      // Prepare recommendations as JSON
      const recsJson = recommendations.map(r => ({
        id: r.id,
        category: r.category,
        title: r.title,
        description: r.description,
        priority: r.priority,
      }));

      const sessionData = {
        user_id: user.id,
        patient_id: formData.patient_id,
        session_date: formData.session_date,
        consultation_type: formData.consultation_type,
        eye_observed: formData.eye_observed,
        patient_gender: formData.patient_gender,
        is_pregnant: formData.is_pregnant,
        is_breastfeeding: formData.is_breastfeeding,
        terrain_nervous: formData.terrain_nervous,
        terrain_digestive: formData.terrain_digestive,
        terrain_circulatory: formData.terrain_circulatory,
        terrain_eliminatory: formData.terrain_eliminatory,
        terrain_mixed: formData.terrain_mixed,
        vitality_level: formData.vitality_level,
        vitality_comments: formData.vitality_comments || null,
        adaptation_good: formData.adaptation_good,
        adaptation_stress_sensitivity: formData.adaptation_stress_sensitivity,
        adaptation_fatigue: formData.adaptation_fatigue,
        sensitivity_zones: formData.sensitivity_zones || null,
        support_zones: formData.support_zones || null,
        overload_digestive: formData.overload_digestive,
        overload_nervous: formData.overload_nervous,
        overload_emotional: formData.overload_emotional,
        overload_eliminatory: formData.overload_eliminatory,
        overload_circulatory: formData.overload_circulatory,
        axis_hygiene: formData.axis_hygiene,
        axis_nutrition: formData.axis_nutrition,
        axis_plants: formData.axis_plants,
        axis_stress: formData.axis_stress,
        axis_seasonal: formData.axis_seasonal,
        additional_observations: formData.additional_observations || null,
        recommendations: recsJson as unknown as any,
      };

      let currentSessionId = sessionIdState;

      if (isNew) {
        const { data, error } = await supabase
          .from("iridology_sessions")
          .insert(sessionData)
          .select()
          .single();

        if (error) throw error;
        currentSessionId = data.id;
        setSessionIdState(data.id);
        toast.success("Séance créée avec succès");
        navigate(`/iridology/${data.id}`);
      } else {
        const { error } = await supabase
          .from("iridology_sessions")
          .update(sessionData)
          .eq("id", id);

        if (error) throw error;
        toast.success("Séance enregistrée");
      }

      // Save observations data
      if (currentSessionId) {
        await saveObservations(currentSessionId, user.id);
      }
    } catch (error: any) {
      console.error("Erreur sauvegarde:", error);
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const saveObservations = async (sessionId: string, userId: string) => {
    try {
      const obsData = {
        session_id: sessionId,
        user_id: userId,
        elements_remarquables: observationsData.elements_remarquables || null,
        aspect_general: observationsData.aspect_general || null,
        couleur: observationsData.couleur || null,
        trame: observationsData.trame || null,
        pupilles: observationsData.pupilles || null,
        anisocorie: observationsData.anisocorie,
        heterochromie: observationsData.heterochromie,
        strabisme: observationsData.strabisme,
        debut_cataracte: observationsData.debut_cataracte,
        autre_observation: observationsData.autre_observation || null,
        myopie: observationsData.myopie,
        hypermetropie: observationsData.hypermetropie,
        astigmatisme: observationsData.astigmatisme,
        presbytie: observationsData.presbytie,
        autre_defaut_vision: observationsData.autre_defaut_vision || null,
        lunettes_vue: observationsData.lunettes_vue,
        lunettes_vue_quand: observationsData.lunettes_vue_quand || null,
        lentilles: observationsData.lentilles,
        lunettes_repos: observationsData.lunettes_repos,
        constitution: observationsData.constitution || null,
        sous_constitution: observationsData.sous_constitution || null,
        trame_od: observationsData.trame_od,
        trame_og: observationsData.trame_og,
        relief_od: observationsData.relief_od || null,
        relief_og: observationsData.relief_og || null,
        cercle_pupillaire_visible: observationsData.cercle_pupillaire_visible || null,
        parasympathique: observationsData.parasympathique || null,
        orthosympathique: observationsData.orthosympathique || null,
        pupille_d_anisocorie: observationsData.pupille_d_anisocorie,
        pupille_d_diametre: observationsData.pupille_d_diametre || null,
        pupille_d_bords_grignotes: observationsData.pupille_d_bords_grignotes,
        pupille_d_bords_cabosses: observationsData.pupille_d_bords_cabosses,
        pupille_d_meplats: observationsData.pupille_d_meplats || null,
        pupille_d_ovalisation: observationsData.pupille_d_ovalisation || null,
        pupille_d_ovalisation_secteurs: observationsData.pupille_d_ovalisation_secteurs || null,
        pupille_d_decentrement: observationsData.pupille_d_decentrement || null,
        pupille_d_decentrement_secteurs: observationsData.pupille_d_decentrement_secteurs || null,
        pupille_g_diametre: observationsData.pupille_g_diametre || null,
        pupille_g_bords_grignotes: observationsData.pupille_g_bords_grignotes,
        pupille_g_bords_cabosses: observationsData.pupille_g_bords_cabosses,
        pupille_g_meplats: observationsData.pupille_g_meplats || null,
        pupille_g_ovalisation: observationsData.pupille_g_ovalisation || null,
        pupille_g_ovalisation_secteurs: observationsData.pupille_g_ovalisation_secteurs || null,
        pupille_g_decentrement: observationsData.pupille_g_decentrement || null,
        pupille_g_decentrement_secteurs: observationsData.pupille_g_decentrement_secteurs || null,
        acide_oxalique: observationsData.acide_oxalique,
        acide_pyruvique: observationsData.acide_pyruvique,
        acide_urique: observationsData.acide_urique,
        anneau_electrolytique: observationsData.anneau_electrolytique,
        anneau_lipidique: observationsData.anneau_lipidique,
        anneau_gerontoxon: observationsData.anneau_gerontoxon,
        taches_psores: observationsData.taches_psores,
        taches_cholesterol: observationsData.taches_cholesterol,
        taches_cicatrisation: observationsData.taches_cicatrisation,
        taches_stress: observationsData.taches_stress,
        taches_hemorragique: observationsData.taches_hemorragique,
        radii_solari: observationsData.radii_solari,
        arcs_cercle: observationsData.arcs_cercle,
        vacuoles: observationsData.vacuoles,
        lacunes: observationsData.lacunes,
        cryptes: observationsData.cryptes,
        conclusions_psy: observationsData.conclusions_psy || null,
      };

      // Always use upsert with session_id unique constraint
      const { data, error } = await supabase
        .from("iridology_observations")
        .upsert(obsData, { onConflict: "session_id" })
        .select("id")
        .single();

      if (error) throw error;
      if (data) setObservationsId(data.id);
    } catch (error: any) {
      console.error("Erreur sauvegarde observations:", error);
      toast.error("Erreur lors de la sauvegarde des observations: " + (error.message || "Erreur inconnue"));
      throw error; // Re-throw so parent handleSave knows it failed
    }
  };

  const handleObservationsChange = (changes: Partial<IridologyObservationsData>) => {
    setObservationsData(prev => ({ ...prev, ...changes }));
  };

  const handlePhotoCapture = async (blob: Blob, eyeSide: 'right' | 'left') => {
    if (isNew) {
      toast.error("Veuillez d'abord enregistrer la séance");
      return;
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Non authentifié");

      const fileName = `${user.id}/${id}/${eyeSide}_${Date.now()}.jpg`;
      
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from("iris-photos")
        .upload(fileName, blob, { contentType: 'image/jpeg' });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from("iris-photos")
        .getPublicUrl(fileName);

      // Since the bucket is private, we need to get a signed URL
      const { data: signedUrlData } = await supabase.storage
        .from("iris-photos")
        .createSignedUrl(fileName, 60 * 60 * 24 * 365); // 1 year

      const photoUrl = signedUrlData?.signedUrl || publicUrl;

      const { data, error } = await supabase
        .from("iris_photos")
        .insert({
          session_id: id,
          user_id: user.id,
          eye_side: eyeSide,
          photo_url: photoUrl,
          photo_order: photos.filter(p => p.eye_side === eyeSide).length + 1,
        })
        .select()
        .single();

      if (error) throw error;

      setPhotos(prev => [...prev, data]);
      toast.success("Photo enregistrée");
    } catch (error: any) {
      console.error("Erreur upload photo:", error);
      toast.error("Erreur lors de l'enregistrement de la photo");
    }
  };

  const handlePhotoUpload = async (file: File, eyeSide: 'right' | 'left') => {
    if (isNew) {
      toast.error("Veuillez d'abord enregistrer la séance");
      return;
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Non authentifié");

      const ext = file.name.split('.').pop();
      const fileName = `${user.id}/${id}/${eyeSide}_${Date.now()}.${ext}`;
      
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from("iris-photos")
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      const { data: signedUrlData } = await supabase.storage
        .from("iris-photos")
        .createSignedUrl(fileName, 60 * 60 * 24 * 365);

      const photoUrl = signedUrlData?.signedUrl || '';

      const { data, error } = await supabase
        .from("iris_photos")
        .insert({
          session_id: id,
          user_id: user.id,
          eye_side: eyeSide,
          photo_url: photoUrl,
          photo_order: photos.filter(p => p.eye_side === eyeSide).length + 1,
        })
        .select()
        .single();

      if (error) throw error;

      setPhotos(prev => [...prev, data]);
      toast.success("Photo importée");
    } catch (error: any) {
      console.error("Erreur upload:", error);
      toast.error("Erreur lors de l'import de la photo");
    }
  };

  const handleDeletePhoto = async (photoId: string) => {
    try {
      const { error } = await supabase
        .from("iris_photos")
        .delete()
        .eq("id", photoId);

      if (error) throw error;

      setPhotos(prev => prev.filter(p => p.id !== photoId));
      toast.success("Photo supprimée");
    } catch (error: any) {
      console.error("Erreur suppression:", error);
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleUpdatePhotoNotes = async (photoId: string, notes: string) => {
    try {
      const { error } = await supabase
        .from("iris_photos")
        .update({ notes })
        .eq("id", photoId);

      if (error) throw error;

      setPhotos(prev => prev.map(p => 
        p.id === photoId ? { ...p, notes } : p
      ));
      toast.success("Notes enregistrées");
    } catch (error: any) {
      console.error("Erreur mise à jour notes:", error);
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const handleExportPdf = async () => {
    try {
      const patient = patients.find(p => p.id === formData.patient_id);
      
      // Récupérer les infos du praticien
      const { data: { user } } = await supabase.auth.getUser();
      let signatureUrl: string | undefined;
      let practitionerInfo: { practitioner_name?: string; address?: string; phone?: string; email?: string } | undefined;
      if (user) {
        const { data: pInfo } = await supabase
          .from("practitioner_info")
          .select("signature_url, practitioner_name, address, phone, email")
          .eq("user_id", user.id)
          .single();
        signatureUrl = pInfo?.signature_url || undefined;
        if (pInfo) {
          practitionerInfo = {
            practitioner_name: pInfo.practitioner_name,
            address: pInfo.address,
            phone: pInfo.phone,
            email: pInfo.email,
          };
        }
      }

      await generateFullSessionPdf({
        patientName: patient?.name || patientName || "Consultant",
        sessionDate: formData.session_date,
        formData,
        photos,
        recommendations,
        rightEye: rightEyeData || undefined,
        leftEye: leftEyeData || undefined,
        zoneObservations,
        selectedZones: selectedZonesForExport,
        signatureUrl,
        observationsData,
        practitionerInfo,
      });
      toast.success("PDF complet généré avec succès");
    } catch (error: any) {
      console.error("Erreur export PDF:", error);
      toast.error("Erreur lors de la génération du PDF");
    }
  };

  const handleDelete = async () => {
    try {
      const { error } = await supabase
        .from("iridology_sessions")
        .delete()
        .eq("id", id);

      if (error) throw error;

      toast.success("Séance supprimée");
      navigate("/iridology");
    } catch (error: any) {
      console.error("Erreur suppression:", error);
      toast.error("Erreur lors de la suppression");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-background to-muted/30">
        <ProNavigation />
        <div className="container mx-auto px-4 py-6">
          <Skeleton className="h-8 w-64 mb-6" />
          <Skeleton className="h-96 w-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/30">
      <ProNavigation />
      
      <div className="container mx-auto px-4 py-6 sm:py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div className="flex items-center gap-3">
            <Link to="/iridology">
              <Button variant="ghost" size="icon">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-primary font-serif flex items-center gap-2">
                <Eye className="w-6 h-6" />
                {isNew ? "Nouvelle séance d'iridologie" : "Séance d'iridologie"}
              </h1>
              {patientName && (
                <p className="text-muted-foreground flex items-center gap-1 mt-1">
                  <User className="w-4 h-4" />
                  {patientName}
                </p>
              )}
            </div>
          </div>
          
          <div className="flex gap-2 w-full sm:w-auto">
            {!isNew && (
              <>
                <Button
                  variant="outline"
                  onClick={handleExportPdf}
                  className="gap-2 flex-1 sm:flex-none"
                >
                  <Download className="w-4 h-4" />
                  <span className="hidden sm:inline">Exporter PDF</span>
                  <span className="sm:hidden">PDF</span>
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="destructive" size="icon">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Supprimer cette séance ?</AlertDialogTitle>
                      <AlertDialogDescription>
                        Cette action est irréversible. La séance et toutes les photos associées seront définitivement supprimées.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Annuler</AlertDialogCancel>
                      <AlertDialogAction onClick={handleDelete}>
                        Supprimer
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </>
            )}
            <Button
              onClick={handleSave}
              disabled={saving}
              className="gap-2 flex-1 sm:flex-none"
            >
              <Save className="w-4 h-4" />
              {saving ? "Enregistrement..." : "Enregistrer"}
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          {/* Mobile: horizontal scroll tabs */}
          <div className="overflow-x-auto -mx-4 px-4 pb-2 sm:mx-0 sm:px-0">
            <TabsList className="inline-flex w-max sm:grid sm:w-full sm:grid-cols-3 lg:grid-cols-6 gap-1 min-w-full sm:min-w-0">
              <TabsTrigger value="info" className="gap-1 sm:gap-2 px-2 sm:px-3 text-xs sm:text-sm whitespace-nowrap">
                <Calendar className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">Informations</span>
                <span className="sm:hidden">Info</span>
              </TabsTrigger>
              <TabsTrigger value="photos" className="gap-1 sm:gap-2 px-2 sm:px-3 text-xs sm:text-sm whitespace-nowrap">
                <Camera className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">Photos</span>
                <span className="sm:hidden">📷</span>
                {photos.length > 0 && (
                  <span className="ml-1 bg-primary/20 text-primary px-1.5 py-0.5 rounded text-[10px] sm:text-xs">
                    {photos.length}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="comparison" className="gap-1 sm:gap-2 px-2 sm:px-3 text-xs sm:text-sm whitespace-nowrap">
                <Eye className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">Comparaison</span>
                <span className="sm:hidden">2 yeux</span>
              </TabsTrigger>
              <TabsTrigger value="observations" className="gap-1 sm:gap-2 px-2 sm:px-3 text-xs sm:text-sm whitespace-nowrap">
                <FileText className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">Observations</span>
                <span className="sm:hidden">Obs</span>
              </TabsTrigger>
              <TabsTrigger value="analysis" className="gap-1 sm:gap-2 px-2 sm:px-3 text-xs sm:text-sm whitespace-nowrap">
                <FileText className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">Analyse</span>
                <span className="sm:hidden">Ana</span>
              </TabsTrigger>
              <TabsTrigger value="recommendations" className="gap-1 sm:gap-2 px-2 sm:px-3 text-xs sm:text-sm whitespace-nowrap">
                <Sparkles className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">Recommandations</span>
                <span className="sm:hidden">Reco</span>
                {recommendations.length > 0 && (
                  <span className="ml-1 bg-primary/20 text-primary px-1.5 py-0.5 rounded text-[10px] sm:text-xs">
                    {recommendations.length}
                  </span>
                )}
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Informations Tab */}
          <TabsContent value="info" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Informations de la séance</CardTitle>
                <CardDescription>
                  Renseignez les informations générales de la séance d'iridologie
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Patient Selection */}
                <div className="space-y-2">
                  <Label htmlFor="patient">Consultant *</Label>
                  <Select
                    value={formData.patient_id}
                    onValueChange={(value) => {
                      handleFormChange({ patient_id: value });
                      const patient = patients.find(p => p.id === value);
                      setPatientName(patient?.name || "");
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner un consultant" />
                    </SelectTrigger>
                    <SelectContent>
                      {patients.map((patient) => (
                        <SelectItem key={patient.id} value={patient.id}>
                          {patient.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {patients.length === 0 && (
                    <p className="text-sm text-muted-foreground">
                      Aucun consultant trouvé.{" "}
                      <Link to="/patients" className="text-primary hover:underline">
                        Créer un consultant
                      </Link>
                    </p>
                  )}
                </div>

                {/* Date */}
                <div className="space-y-2">
                  <Label htmlFor="session_date">Date de la séance</Label>
                  <Input
                    type="date"
                    id="session_date"
                    value={formData.session_date}
                    onChange={(e) => handleFormChange({ session_date: e.target.value })}
                  />
                </div>

                {/* Consultation Type */}
                <div className="space-y-2">
                  <Label>Type de consultation</Label>
                  <RadioGroup
                    value={formData.consultation_type}
                    onValueChange={(value) => handleFormChange({ consultation_type: value as any })}
                    className="flex flex-col gap-3"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="iridology_only" id="type-iridology" />
                      <Label htmlFor="type-iridology" className="cursor-pointer">
                        Iridologie seule
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="iridology_naturopathy" id="type-both" />
                      <Label htmlFor="type-both" className="cursor-pointer">
                        Iridologie + accompagnement naturopathique
                      </Label>
                    </div>
                  </RadioGroup>
                </div>

                {/* Eye Observed */}
                <div className="space-y-2">
                  <Label>Œil observé</Label>
                  <RadioGroup
                    value={formData.eye_observed}
                    onValueChange={(value) => handleFormChange({ eye_observed: value as any })}
                    className="flex flex-wrap gap-4"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="right" id="eye-right" />
                      <Label htmlFor="eye-right" className="cursor-pointer">Œil droit</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="left" id="eye-left" />
                      <Label htmlFor="eye-left" className="cursor-pointer">Œil gauche</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="both" id="eye-both" />
                      <Label htmlFor="eye-both" className="cursor-pointer">Les deux</Label>
                    </div>
                  </RadioGroup>
                </div>

                {/* Gender */}
                <div className="space-y-2">
                  <Label>Sexe du consultant</Label>
                  <RadioGroup
                    value={formData.patient_gender || ''}
                    onValueChange={(value) => {
                      handleFormChange({ 
                        patient_gender: value as 'male' | 'female',
                        // Reset pregnancy/breastfeeding if changing to male
                        ...(value === 'male' ? { is_pregnant: false, is_breastfeeding: false } : {})
                      });
                    }}
                    className="flex flex-wrap gap-4"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="male" id="gender-male" />
                      <Label htmlFor="gender-male" className="cursor-pointer">Homme</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="female" id="gender-female" />
                      <Label htmlFor="gender-female" className="cursor-pointer">Femme</Label>
                    </div>
                  </RadioGroup>
                </div>

                {/* Pregnancy/Breastfeeding options - only show for female */}
                {formData.patient_gender === 'female' && (
                  <div className="space-y-3 pl-4 border-l-2 border-primary/20">
                    <Label className="text-sm text-muted-foreground">Situation particulière</Label>
                    <div className="flex flex-wrap gap-4">
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="is_pregnant"
                          checked={formData.is_pregnant}
                          onCheckedChange={(checked) => handleFormChange({ is_pregnant: !!checked })}
                        />
                        <Label htmlFor="is_pregnant" className="cursor-pointer text-sm">
                          Enceinte
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="is_breastfeeding"
                          checked={formData.is_breastfeeding}
                          onCheckedChange={(checked) => handleFormChange({ is_breastfeeding: !!checked })}
                        />
                        <Label htmlFor="is_breastfeeding" className="cursor-pointer text-sm">
                          Allaitante
                        </Label>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Photos Tab */}
          <TabsContent value="photos" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Camera className="w-5 h-5" />
                  Photos de l'iris
                </CardTitle>
                <CardDescription>
                  Prenez des photos directement ou importez des images existantes
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {isNew ? (
                  <div className="text-center py-8 text-muted-foreground bg-muted/30 rounded-lg">
                    <Camera className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p className="text-sm">Enregistrez d'abord la séance pour pouvoir ajouter des photos</p>
                  </div>
                ) : (
                  <>
                    <IrisCameraCapture
                      onCapture={handlePhotoCapture}
                      onUpload={handlePhotoUpload}
                    />
                    
                    <div className="border-t pt-6">
                      <h4 className="font-medium mb-4">Photos enregistrées</h4>
                      <IrisPhotoGallery
                        photos={photos}
                        onDelete={handleDeletePhoto}
                        onUpdateNotes={handleUpdatePhotoNotes}
                      />
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Comparison Tab - Both Eyes Side by Side */}
          <TabsContent value="comparison" className="space-y-6">
            <IrisComparisonView 
              sessionId={sessionIdState || undefined}
              patientName={patientName || "Patient"}
              sessionPhotos={photos}
              selectedZonesForExport={selectedZonesForExport}
              onSelectedZonesChange={setSelectedZonesForExport}
              onEyeDataChange={handleEyeDataChange}
              onPhotoSaved={(photo) => setPhotos((prev) => [...prev, photo])}
            />
          </TabsContent>

          {/* Observations Tab */}
          <TabsContent value="observations" className="space-y-6">
            <IridologyObservationsForm
              data={observationsData}
              onChange={handleObservationsChange}
              readOnly={false}
            />
          </TabsContent>

          {/* Analysis Tab */}
          <TabsContent value="analysis" className="space-y-6">
            <IridologyAnalysisForm
              data={formData}
              onChange={handleFormChange}
            />
          </TabsContent>

          {/* Recommendations Tab */}
          <TabsContent value="recommendations" className="space-y-6">
            {isNew || !sessionIdState ? (
              <Card>
                <CardContent className="py-12 text-center text-muted-foreground">
                  <Sparkles className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>Enregistrez d'abord la séance pour gérer les recommandations</p>
                </CardContent>
              </Card>
            ) : (
              <IridologyRecommendations
                formData={formData}
                sessionId={sessionIdState}
                recommendations={recommendations}
                onRecommendationsChange={setRecommendations}
              />
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default IridologySession;
